import {
    groupBannersQuery,
    reachIndoorQuery,
    reachOutdoorQuery,
    reachQuery,
    reachRecommendationQuery
} from '../../graphql/queries/grid/domain';
import { mapGroupTypeToGroupTypeInput } from '../../mappers/common/common';
import { mapSizeBannersFromApiFormats } from '../../mappers/predictor';
import { GroupType } from '../../typings/domain';
import {
    GdBidModifierDemographicInput,
    GdIndoorReachRequestInput,
    GdOutdoorReachRequestInput,
    GdPageBlockInput,
    GdReachRequestInput,
    GdRetargetingConditionRuleItemReqInput,
    GdStatPresetInput,
    GroupBannersQuery,
    ReachIndoorQuery,
    ReachIndoorQuery_reachIndoor,
    ReachOutdoorQuery,
    ReachOutdoorQuery_reachOutdoor,
    ReachQuery,
    ReachQuery_reach_basic,
    ReachQuery_reach_detailed,
    ReachRecommendationQuery
} from '../../typings/graphql-grid';
import { BannerFormats, BannerSize, PlatformType, ReachResultValue, StatusReach } from '../../typings/predictor';
import { BaseDataProvider } from '../BaseDataProvider/BaseDataProvider';

export type FetchProviderResult = {
    id: string;
    basic?: ReachResultValue;
    detailed?: ReachResultValue;
};

export type FetchProviderRecommendationResult = {
    bannersFormats?: BannerFormats[];
};

export type FetchProviderOutdoorResult = {
    data: ReachResultValue;
    otsData?: ReachResultValue;
    id: string;
};

export class PredictorProvider extends BaseDataProvider {
    public fetchPredictorData = (
        campainId: string,
        groupId: string,
        groupType: GroupType,
        geo: number[] | null,
        isNew: boolean,
        platformType: PlatformType,
        conditions?: GdRetargetingConditionRuleItemReqInput[]
    ): Promise<FetchProviderResult> => {
        const input: GdReachRequestInput = {
            adgroupId: isNew ? null : groupId,
            campaignId: campainId,
            geo: geo,
            excludedDomains: [],
            groupType: mapGroupTypeToGroupTypeInput(groupType)
        };
        const { value: platform, empty } = platformType;

        if (platform && !empty) {
            input.platformCorrectionsWeb = platform;
        }

        if (conditions && conditions.length) {
            input.conditions = conditions;
        }

        return this.createQuery<ReachQuery>(reachQuery, {
            variables: {
                input: input
            }
        }).then(
            (result): FetchProviderResult => {
                const { requestId, basic, detailed } = result.data.reach;

                return {
                    id: requestId,
                    basic: basic ? getInfoFromResult(basic) : undefined,
                    detailed: detailed ? getInfoFromResult(detailed) : undefined
                };
            }
        );
    };

    public fetchPredictorRecommendationData = (
        campainId: string,
        groupId: string,
        groupType: GroupType,
        geo: number[] | null,
        isNew: boolean,
        platformType: PlatformType,
        conditions?: GdRetargetingConditionRuleItemReqInput[]
    ): Promise<FetchProviderRecommendationResult> => {
        const input: GdReachRequestInput = {
            adgroupId: isNew ? null : groupId,
            campaignId: campainId,
            geo: geo,
            excludedDomains: [],
            groupType: mapGroupTypeToGroupTypeInput(groupType)
        };
        const { value: platform, empty } = platformType;

        if (platform && !empty) {
            input.platformCorrectionsWeb = platform;
        }

        if (conditions && conditions.length) {
            input.conditions = conditions;
        }

        return this.createQuery<ReachRecommendationQuery>(reachRecommendationQuery, {
            variables: {
                input: input
            }
        }).then(
            (result): FetchProviderRecommendationResult => {
                const banners = result.data.reachRecommendation.bannerFormats;

                return {
                    bannersFormats: banners
                        ? banners.map(banner => {
                              return {
                                  format: banner.format,
                                  increasePercent: Number(banner.increasePercent)
                              };
                          })
                        : undefined
                };
            }
        );
    };

    public getGroupBanners = (
        login: string,
        campaignId: string,
        groupsId: string
    ): Promise<BannerSize[] | undefined> => {
        return this.createQuery<GroupBannersQuery>(groupBannersQuery, {
            variables: {
                login: login,
                adsInput: {
                    statRequirements: { preset: GdStatPresetInput.TODAY },
                    orderBy: [],
                    filter: {
                        campaignIdIn: campaignId,
                        adGroupIdIn: groupsId
                    },
                    limitOffset: {
                        offset: 0,
                        limit: 1000
                    }
                }
            }
        }).then(mapSizeBannersFromApiFormats);
    };

    public fetchPredictorOutdoorData = (
        campainId: string,
        groupId: string,
        isNew: boolean,
        pageBlocks?: GdPageBlockInput[]
    ): Promise<FetchProviderOutdoorResult | undefined> => {
        const input: GdOutdoorReachRequestInput = {
            campaignId: campainId,
            adGroupId: isNew ? null : groupId,
            pageBlocks: pageBlocks
        };
        return this.createQuery<ReachOutdoorQuery>(reachOutdoorQuery, {
            variables: {
                input: input
            }
        }).then((result): FetchProviderOutdoorResult | undefined => {
            if (!result.data.reachOutdoor) {
                return;
            }

            const { requestId } = result.data.reachOutdoor;

            return {
                id: requestId,
                data: getInfoFromResult(result.data.reachOutdoor),
                otsData: getInfoFromResult(result.data.reachOutdoor, 'otsCapacity')
            };
        });
    };

    public fetchPredictorIndoorData = (
        campainId: string,
        groupId: string,
        isNew: boolean,
        pageBlocks?: GdPageBlockInput[],
        adjustment?: GdBidModifierDemographicInput[]
    ): Promise<FetchProviderOutdoorResult | undefined> => {
        const input: GdIndoorReachRequestInput = {
            campaignId: campainId,
            adGroupId: isNew ? null : groupId,
            pageBlocks: pageBlocks,
            bidModifierDemographic: adjustment || null
        };

        return this.createQuery<ReachIndoorQuery>(reachIndoorQuery, {
            variables: {
                input: input
            }
        }).then((result): FetchProviderOutdoorResult | undefined => {
            if (!result.data.reachIndoor) {
                return;
            }

            const { requestId } = result.data.reachIndoor;

            return {
                id: requestId,
                data: getInfoFromResult(result.data.reachIndoor, 'otsCapacity')
            };
        });
    };
}

const getInfoFromResult = (
    result:
        | ReachQuery_reach_basic
        | ReachQuery_reach_detailed
        | ReachOutdoorQuery_reachOutdoor
        | ReachIndoorQuery_reachIndoor,
    type?: string
): ReachResultValue => {
    let status;
    const reach = type ? result[type] : result.reach;

    if (reach) {
        status = StatusReach.NORMAL;
    } else if (result.reachLessThan) {
        status = StatusReach.LESS;
    } else {
        status = StatusReach.EMPTY;
    }

    const value = reach || result.reachLessThan;

    return {
        status: status,
        value: value === null ? undefined : Number(value)
    };
};
